import { AxiosInstance } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { BackendApiService } from 'services/BackendApi';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import {
  ICreateMultiRequestBody,
  TCreateMultiResult,
  IMultiBookingInformation,
  IMultiBookingItem,
  TMultiBookingStatus,
  TMultiPaymentMethod,
} from 'services/BackendApi/types/MultiBooking';
import { iActingOnBehalfOfUser } from 'store/modules/actingOnBehalfOf/model';
import { IBookingInformation, TCountryCode } from 'interfaces';
import { isOnHold } from './utils';

export const getEffectiveBuilderResponse = (build: IBasketBuildL4) =>
  isOnHold(build) ? build.initialBuildResponse : build.latestBookingBuilderResponse ?? build.initialBuildResponse;

export interface ISelectionLockTuple {
  defaultCurrency: string;
  paymentCurrency: string | null | undefined;
}

export const getLockTuple = (build: IBasketBuildL4): ISelectionLockTuple => {
  const response = getEffectiveBuilderResponse(build);
  return {
    defaultCurrency: response.hotel.defaultCurrency,
    paymentCurrency: build.paymentCurrency ?? null,
  };
};

export const lockTuplesMatch = (a: ISelectionLockTuple, b: ISelectionLockTuple): boolean =>
  a.defaultCurrency === b.defaultCurrency && (a.paymentCurrency ?? null) === (b.paymentCurrency ?? null);

/**
 * Weighted margin per ticket §3.5. Returns a 5-decimal string.
 *  - Case A (no item on request): Σ(saleTotal_i × margin_i) / Σ(saleTotal_i)
 *  - Case B (any on request):     Σ(margin_i) / count
 *  - Σ saleTotal === 0 → 0
 */
export const computeWeightedMargin = (builds: IBasketBuildL4[]): string => {
  if (builds.length === 0) return (0).toFixed(5);

  const rows = builds.map(b => {
    const response = getEffectiveBuilderResponse(b);
    return {
      saleTotal: parseFloat(response.totals.total || '0'),
      marginPercent: parseFloat(b.taMarginAmount || '0'),
      onRequest: response.totals.oneOrMoreItemsOnRequest === true,
    };
  });

  const anyOnRequest = rows.some(r => r.onRequest);
  if (anyOnRequest) {
    const sum = rows.reduce((acc, r) => acc + r.marginPercent, 0);
    return (sum / rows.length).toFixed(5);
  }

  const totalSale = rows.reduce((acc, r) => acc + r.saleTotal, 0);
  if (totalSale === 0) {
    // All priced items sum to zero — fall back to simple mean to avoid NaN.
    const sum = rows.reduce((acc, r) => acc + r.marginPercent, 0);
    return (sum / rows.length).toFixed(5);
  }

  const weighted = rows.reduce((acc, r) => acc + r.saleTotal * r.marginPercent, 0) / totalSale;
  return weighted.toFixed(5);
};

export interface ICombinedTotalDisplay {
  isOnRequest: boolean;
  total: number;
  currency: string;
}

export const getCombinedTotalDisplay = (builds: IBasketBuildL4[]): ICombinedTotalDisplay => {
  const anyOnRequest = builds.some(b => getEffectiveBuilderResponse(b).totals.oneOrMoreItemsOnRequest === true);
  const total = builds.reduce((acc, b) => acc + parseFloat(getEffectiveBuilderResponse(b).totals.total || '0'), 0);
  const currency = builds.length > 0 ? (getEffectiveBuilderResponse(builds[0]).hotel.defaultCurrency) : '';
  return { isOnRequest: anyOnRequest, total, currency };
};

export const allBuildsShareCurrency = (builds: IBasketBuildL4[]): boolean => {
  if (builds.length <= 1) return true;
  const first = getLockTuple(builds[0]);
  return builds.slice(1).every(b => lockTuplesMatch(first, getLockTuple(b)));
};

const buildMultiItems = (builds: IBasketBuildL4[]): IMultiBookingItem[] =>
  builds.map(b => {
    const response = getEffectiveBuilderResponse(b);
    return {
      basketBuildUuid: b.uuid,
      bookingBuild: b.buildRequest,
      bookingHash: response.bookingHash ?? '',
    };
  });

const buildMultiBookingInformation = (
  guestInfo: IBookingInformation,
  taMarginAmount: string,
  selectedTaUuid?: string
): IMultiBookingInformation => ({
  guestTitle: guestInfo.guestTitle || undefined,
  guestFirstName: guestInfo.guestFirstName!,
  guestLastName: guestInfo.guestLastName!,
  isRepeatGuest: false,
  specialRequests: guestInfo.specialRequests,
  comments: guestInfo.comments,
  flightArrivalDate: guestInfo.flightArrivalDate,
  flightArrivalNumber: guestInfo.flightArrivalNumber,
  flightDepartureDate: guestInfo.flightDepartureDate,
  flightDepartureNumber: guestInfo.flightDepartureNumber,
  travelAgentUserUuid: selectedTaUuid,
  taMarginType: 'percentage',
  taMarginAmount,
});

interface IBuildMultiPayloadArgs {
  builds: IBasketBuildL4[];
  guestInfo: IBookingInformation;
  status: TMultiBookingStatus;
  selectedTaUuid?: string;
  companyMembershipUuid?: string | null;
  paymentMethod?: TMultiPaymentMethod;
  isQuote?: boolean;
}

export const buildCreateMultiPayload = (args: IBuildMultiPayloadArgs): ICreateMultiRequestBody => {
  const { builds, guestInfo, status, selectedTaUuid, companyMembershipUuid, paymentMethod, isQuote } = args;
  const taMarginAmount = computeWeightedMargin(builds);
  return {
    data: {
      attributes: {
        items: buildMultiItems(builds),
        bookingInformation: buildMultiBookingInformation(guestInfo, taMarginAmount, selectedTaUuid),
        ...(paymentMethod ? { paymentInformation: { method: paymentMethod } } : {}),
        status,
        companyMembershipUuid: companyMembershipUuid ?? null,
        isQuote: isQuote ?? false,
      },
    },
  };
};

interface ISubmitArgs {
  backendApi: BackendApiService<AxiosWrapper<AxiosInstance>>;
  payload: ICreateMultiRequestBody;
  clientCountryCode: TCountryCode | null;
  actingOnBehalfOfUser: iActingOnBehalfOfUser | null;
}

export const submitMultiBooking = async ({
  backendApi,
  payload,
  clientCountryCode,
  actingOnBehalfOfUser,
}: ISubmitArgs): Promise<TCreateMultiResult> =>
  backendApi.createMultiBooking(payload, clientCountryCode, actingOnBehalfOfUser);
